import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FiAlertTriangle, FiAlertOctagon, FiX, FiCopy, FiCheck } from 'react-icons/fi';
import { BASE_URL } from '../constants/config';
import { findPlayerDuplicates } from '../actions/playersActions';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import '../styles/editModal.css';

// Проверка игроков на дубли через POST /players/findDuplicates.
//
// Для нового игрока (без player_id) — капитан мог завести его заново, хотя он
// уже есть в базе. Для игрока из базы — у человека может быть второй профиль.
// В обоих случаях ищем ДРУГИЕ профили с тем же ФИО и смотрим их заявки:
//
//   same_team        — такой профиль уже в составе ЭТОЙ команды в ЭТОЙ заявке;
//   same_application — такой профиль в этой заявке, но за другую команду;
//   base             — в базе есть, но в эту заявку не заявлен;
//   none             — совпадений нет.
//
// Найденные дубли админ копирует и пересылает разработчикам на объединение.

export const DUP_LEVELS = {
    none: 0,
    base: 1,
    same_application: 2,
    same_team: 3,
};

const normalize = (value) => String(value || '').toLowerCase().replace(/ё/g, 'е').trim();

const levelOfMatch = (apps, applicationId, teamId) => {
    const list = Array.isArray(apps) ? apps : [];
    const inApplication = list.filter(app =>
        applicationId && String(app.application_id) === String(applicationId));
    const inTeam = inApplication.filter(app =>
        teamId && String(app.team_id) === String(teamId));
    if (inTeam.length) return 'same_team';
    if (inApplication.length) return 'same_application';
    return 'base';
};

const mapApiMatches = (apiMatches, applicationId, teamId) => {
    const matches = (Array.isArray(apiMatches) ? apiMatches : []).map(one => {
        const apps = one.applications || [];
        const id = one.id || one.player_id;
        return {
            player: one,
            id,
            apps,
            level: levelOfMatch(apps, applicationId, teamId),
        };
    });
    matches.sort((a, b) => DUP_LEVELS[b.level] - DUP_LEVELS[a.level]);
    return matches;
};

const resultFromRow = (row, applicationId, teamId, selfId) => {
    const matches = mapApiMatches(row && row.matches, applicationId, teamId)
        .filter(m => !selfId || String(m.id) !== String(selfId));
    if (matches.length === 0) return { level: 'none', matches: [] };
    return { level: matches[0].level, matches };
};

// На странице заявки монтируется десятки хинтов сразу — склеиваем их
// в один findDuplicates за короткий тик, а не шлём N одинаковых запросов
const BATCH_MS = 40;
let pendingChecks = [];
let batchTimer = null;

const flushDuplicateBatch = async () => {
    batchTimer = null;
    const batch = pendingChecks;
    pendingChecks = [];
    if (!batch.length) return;

    const groups = new Map();
    batch.forEach(item => {
        const key = `${item.applicationId || ''}|${item.teamId || ''}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(item);
    });

    for (const items of groups.values()) {
        const { dispatch, applicationId, teamId } = items[0];
        const players = items.map(({ family, name, selfId }, index) => {
            const entry = { family, name, _idx: index };
            if (selfId) entry.player_id = selfId;
            return entry;
        });
        try {
            const rows = await dispatch(findPlayerDuplicates({
                applicationId,
                teamId,
                players: players.map(({ family, name, player_id }) => (
                    player_id ? { family, name, player_id } : { family, name }
                )),
            }));
            // Строку ответа ищем по player_id, затем по ФИО (если сервер его вернул),
            // по позиции — только когда ответ ровно на каждого игрока. Подставлять
            // «первую свободную» нельзя: игроку показались бы дубли соседа
            const list = Array.isArray(rows) ? rows : [];
            const sameOrder = list.length === items.length;
            items.forEach((item, index) => {
                let row = null;
                if (item.selfId) {
                    row = list.find(r => String(r.player_id) === String(item.selfId)) || null;
                }
                if (!row) {
                    row = list.find(r => r.family && r.name
                        && normalize(r.family) === normalize(item.family)
                        && normalize(r.name) === normalize(item.name)) || null;
                }
                if (!row && sameOrder) {
                    const candidate = list[index];
                    const otherPlayer = candidate && candidate.player_id && item.selfId
                        && String(candidate.player_id) !== String(item.selfId);
                    if (!otherPlayer) row = candidate;
                }
                item.resolve(resultFromRow(row, applicationId, teamId, item.selfId));
            });
        } catch (e) {
            console.error('Не удалось проверить игроков на дубли:', e);
            items.forEach(item => item.resolve({ level: 'none', matches: [] }));
        }
    }
};

// Полная проверка одного игрока через batch-рут (один запрос вместо search + N apps)
export const checkDuplicates = async (dispatch, player, applicationId, teamId, selfId) => {
    const family = (player.family || '').trim();
    const name = (player.name || '').trim();
    if (family.length < 2 || !name) return { level: 'none', matches: [] };

    return new Promise(resolve => {
        pendingChecks.push({
            dispatch, applicationId, teamId, selfId, family, name, resolve,
        });
        if (!batchTimer) {
            batchTimer = setTimeout(flushDuplicateBatch, BATCH_MS);
        }
    });
};

const playerPhoto = (player) => {
    const image = player.image || player.player_image;
    if (!image) return defaultTeamLogo;
    return image.startsWith('http') ? image : BASE_URL.url + image;
};

const formatDate = (value) => {
    if (!value) return '';
    const parts = String(value).slice(0, 10).split('-');
    if (parts.length !== 3) return String(value);
    return `${parts[2]}.${parts[1]}.${parts[0]}`;
};

const teamNameOf = (app) => app.team_name || app.name || 'Команда';

const fullName = (one) => `${one.family || ''} ${one.name || ''} ${one.father_name || ''}`.replace(/\s+/g, ' ').trim();

// Тексты зависят от того, новый это игрок или уже профиль из базы
const badgeText = (result, linked) => {
    if (result.level === 'same_team') {
        return linked
            ? 'В этой команде есть ещё один профиль с таким ФИО'
            : 'Игрок с таким ФИО уже в составе этой команды';
    }
    if (result.level === 'same_application') {
        const hit = result.matches.find(m => m.level === 'same_application');
        const app = hit && (hit.apps || []).find(a =>
            String(a.application_id) === String(result.applicationId));
        const team = app ? ` · ${teamNameOf(app)}` : '';
        return linked
            ? `В этой заявке есть другой профиль с таким ФИО${team}`
            : `Однофамилец уже заявлен в эту заявку${team}`;
    }
    return linked
        ? `Есть другие профили с таким ФИО (${result.matches.length})`
        : `В базе есть игроки с таким ФИО (${result.matches.length})`;
};

const hintText = (level, linked) => {
    if (level === 'same_team') {
        return linked
            ? 'За эту же команду в этой заявке числится ещё один профиль с таким ФИО. '
              + 'Похоже, у человека два профиля — отметьте их и отправьте на объединение.'
            : 'В этой же заявке за эту же команду уже числится игрок с таким ФИО. '
              + 'Скорее всего, капитан завёл его заново — нового игрока лучше отклонить.';
    }
    if (level === 'same_application') {
        return linked
            ? 'В этой заявке за другую команду есть профиль с таким же ФИО. '
              + 'Если это один человек, он числится в заявке дважды.'
            : 'Игрок с таким ФИО уже заявлен в эту заявку за другую команду. '
              + 'Проверьте, тот ли это человек: если да, он окажется в одной заявке дважды.';
    }
    return linked
        ? 'В базе есть другие профили с таким ФИО. Это может быть однофамилец — сверьте дату '
          + 'рождения и фото. Если это один человек, отправьте профили на объединение.'
        : 'В базе есть игроки с таким ФИО, но в эту заявку они не заявлены. '
          + 'Это может быть однофамилец — сверьте дату рождения и фото.';
};

const copyText = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (e) {
        // Запасной путь для браузеров без clipboard API
        const area = document.createElement('textarea');
        area.value = text;
        document.body.appendChild(area);
        area.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(area);
        return ok;
    }
};

// Значок рядом с ФИО. По клику — список профилей с таким же ФИО, их заявки
// и текст для разработчиков на объединение
export default function DuplicatePlayersHint({
    player, applicationId, teamId, onResult, dispatch: outerDispatch,
}) {
    const innerDispatch = useDispatch();
    const dispatch = outerDispatch || innerDispatch;
    const [result, setResult] = useState({ level: 'none', matches: [] });
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState([]);
    const [mergeState, setMergeState] = useState(null); // null | copied | error
    const [mergeError, setMergeError] = useState(null);

    const selfId = player && player.player_id;
    const linked = Boolean(selfId);

    useEffect(() => {
        let cancelled = false;
        checkDuplicates(dispatch, player || {}, applicationId, teamId, selfId)
            .then(res => {
                if (cancelled) return;
                const withApp = { ...res, applicationId };
                setResult(withApp);
                if (onResult) onResult(withApp);
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selfId, player && player.family, player && player.name, applicationId, teamId]);

    if (result.level === 'none') return null;

    const critical = result.level === 'same_team' || result.level === 'same_application';
    const Icon = critical ? FiAlertOctagon : FiAlertTriangle;

    // Профили, которые уходят на объединение: сам игрок (если он из базы) + отмеченные
    const mergeIds = [...(linked ? [selfId] : []), ...selected];
    const canMerge = mergeIds.length >= 2;

    const toggle = (id) => {
        setMergeState(null);
        setSelected(prev => (prev.some(x => String(x) === String(id))
            ? prev.filter(x => String(x) !== String(id))
            : [...prev, id]));
    };

    const describe = (id) => {
        if (linked && String(id) === String(selfId)) return player;
        const hit = result.matches.find(m => String(m.id) === String(id));
        return hit ? hit.player : {};
    };

    const mergeText = () => [
        'Объединить профили игроков (один человек):',
        ...mergeIds.map(id => {
            const one = describe(id);
            const birthday = formatDate(one.birthday);
            return `— id ${id}: ${fullName(one)}${birthday ? `, др ${birthday}` : ''}`;
        }),
        applicationId ? `Заявка: ${applicationId}${teamId ? `, команда ${teamId}` : ''}` : '',
    ].filter(Boolean).join('\n');

    const copyMerge = async () => {
        const ok = await copyText(mergeText());
        setMergeState(ok ? 'copied' : 'error');
        if (!ok) setMergeError('Не удалось скопировать — выделите текст вручную.');
    };

    return (
        <>
            <button
                type="button"
                className={`dupBadge ${critical ? 'dupBadgeCritical' : ''}`}
                title="Проверка на дубли"
                onClick={(e) => { e.stopPropagation(); setOpen(true); }}
            >
                <Icon />
                <span>{badgeText(result, linked)}</span>
            </button>

            {open ? (
                <div className="modalOverlay" onClick={(e) => { e.stopPropagation(); setOpen(false); }}>
                    <div className="modalContent modalContentWide" onClick={(e) => e.stopPropagation()}>
                        <div className="modalHeader">
                            <h2>Профили с таким же ФИО</h2>
                            <button className="modalClose" onClick={() => setOpen(false)}><FiX /></button>
                        </div>
                        <div className="modalBody modalBodyScroll">
                            <div className={`dupSummary ${critical ? 'dupSummaryCritical' : ''}`}>
                                <div>
                                    {linked ? 'Игрок' : 'Подают'}:{' '}
                                    <strong>{fullName(player)}</strong>
                                    {player.birthday ? `, др ${formatDate(player.birthday)}` : ''}
                                    {linked ? ` · id ${selfId}` : ' · новый игрок'}
                                </div>
                                <div className="dupSummaryText">{hintText(result.level, linked)}</div>
                            </div>

                            {result.matches.map(({ player: one, id, apps, level }) => {
                                const sameFather = normalize(one.father_name) === normalize(player.father_name);
                                const checked = selected.some(x => String(x) === String(id));
                                return (
                                    <div className={`dupRow dupRow_${level}`} key={id}>
                                        <label className="dupPick" title="Это тот же человек">
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggle(id)}
                                            />
                                        </label>
                                        <img
                                            src={playerPhoto(one)}
                                            alt=""
                                            onError={(e) => { e.target.src = defaultTeamLogo; }}
                                        />
                                        <div className="dupRowMain">
                                            <div className="dupRowName">
                                                {fullName(one)}
                                                {player.father_name && one.father_name && !sameFather ? (
                                                    <span className="dupTag dupTagMuted">отчество другое</span>
                                                ) : null}
                                            </div>
                                            <div className="dupRowMeta">
                                                {formatDate(one.birthday) ? `др ${formatDate(one.birthday)}` : 'др не указана'}
                                                {one.position ? ` · ${one.position}` : ''}
                                                {` · id ${id}`}
                                            </div>

                                            <div className="dupApps">
                                                {apps === null ? (
                                                    <span className="dupAppsEmpty">Не удалось загрузить заявки</span>
                                                ) : apps.length === 0 ? (
                                                    <span className="dupAppsEmpty">Не заявлен ни в одну заявку</span>
                                                ) : apps.map((app, index) => {
                                                    const thisApp = applicationId
                                                        && String(app.application_id) === String(applicationId);
                                                    const thisTeam = thisApp && teamId
                                                        && String(app.team_id) === String(teamId);
                                                    return (
                                                        <div
                                                            key={`${app.application_id}-${app.team_id}-${index}`}
                                                            className={`dupApp ${thisApp ? 'dupAppHit' : ''}`}
                                                        >
                                                            <span className="dupAppTeam">{teamNameOf(app)}</span>
                                                            <span className="dupAppName">
                                                                {app.application_name || 'Заявка'}
                                                                {app.application_year ? ` · ${app.application_year}` : ''}
                                                            </span>
                                                            {thisTeam ? (
                                                                <span className="dupTag dupTagCritical">эта команда, эта заявка</span>
                                                            ) : thisApp ? (
                                                                <span className="dupTag dupTagCritical">эта заявка</span>
                                                            ) : null}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <a
                                            className="linkButton"
                                            href={`/player/${id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Открыть карточку
                                        </a>
                                    </div>
                                );
                            })}

                            <div className="dupMerge">
                                <div className="dupMergeTitle">Объединение профилей</div>
                                <div className="dupMergeHint">
                                    {linked
                                        ? 'Отметьте галочкой профили, которые принадлежат этому же человеку.'
                                        : 'Новый игрок ещё не в базе — его лучше отклонить. Здесь можно '
                                          + 'отправить на объединение старые профили-дубли, если они есть.'}
                                    {' '}Скопируйте текст и отправьте разработчикам — объединяют профили они.
                                </div>
                                {canMerge ? (
                                    <pre className="dupMergePreview">{mergeText()}</pre>
                                ) : (
                                    <div className="dupMergeHint dupMergeMuted">
                                        Нужно минимум два профиля.
                                    </div>
                                )}
                                <div className="dupMergeActions">
                                    <button
                                        type="button"
                                        className="dupMergeBtn dupMergeBtnPrimary"
                                        disabled={!canMerge}
                                        onClick={copyMerge}
                                    >
                                        {mergeState === 'copied' ? <FiCheck /> : <FiCopy />}
                                        {mergeState === 'copied' ? 'Скопировано' : 'Скопировать для разработчиков'}
                                    </button>
                                </div>
                                {mergeState === 'error' && mergeError ? (
                                    <div className="dupMergeError">{mergeError}</div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
}
